<template>
   <div style="margin-bottom: 25px" v-if="expenseType === 'employees'">
      <EmployeesManualFlow />
   </div>
   <div style="margin-bottom: 25px" v-else-if="expenseType !== 'contractors'">
      <label class="my-2 modal-subtitle mt-0 pt-0" style="width: 80%"
         >If you have unlisted supply vendors, add them here. Be sure to upload supporting
         documents, as these transactions weren't included in the previous file</label
      >
      <div class="d-flex flex-row justify-content-between mb-2 mt-4" style="width: 100%">
         <h5 style="font-size: 16px" class="font-weight-bold">Vendors</h5>
         <b-button
            class="my-next-ok-btn"
            variant="primary"
            @click="addManualVendor"
            :disabled="isCompleted"
            :class="{'btn-disabled': isCompleted}"
         >
            Add New +
         </b-button>
      </div>

      <div class="d-flex flex-column" style="width: 100%; height: 250px; overflow-y: auto">
         <div class="project-section" v-if="addManually">
            <img
               src="@/assets/img/project.png"
               style="width: 30px; height: 30px; margin-right: 12px"
               alt="project"
            />
            <b-form-group :state="inputState" :invalid-feedback="errorMessage">
               <div class="d-flex">
                  <b-form-group
                     label-for="input"
                     label="Name"
                     style="margin-bottom: 0; font-size: 11px"
                  >
                     <b-form-input
                        id="input"
                        v-model="manuallyAddedName"
                        :state="inputState"
                        @blur="handleBlur"
                        style="width: 200px; height: 30px; margin-top: -5px; font-size: 12px"
                     ></b-form-input>
                  </b-form-group>
                  <b-form-group
                     v-if="hasManyLocations"
                     label-for="location"
                     label="Location"
                     style="margin-bottom: 0; font-size: 11px"
                     class="ml-2"
                  >
                     <b-form-select
                        id="location"
                        v-if="hasManyLocations"
                        :options="stateOptions"
                        v-model="location"
                        style="width: 200px; height: 30px; margin-top: -5px; font-size: 12px"
                        placeholder="Location"
                     >
                     </b-form-select>
                  </b-form-group>
                  <b-button
                     class="my-next-ok-btn ml-2"
                     variant="primary"
                     style="height: 30px; margin-top: 18px"
                     :class="{'btn-disabled': addVendorBtnDisabled}"
                     :disabled="addVendorBtnDisabled"
                     @click="saveVendor"
                     >Add Vendor
                  </b-button>
               </div>
            </b-form-group>
         </div>
         <div
            class="project-section d-flex justify-content-between"
            v-for="{id, name, location} in manualVendors"
            :key="id"
         >
            <div style="display: flex; flex-direction: column">
               <label style="font-size: 14px; font-weight: 600; margin-bottom: 2px">{{
                  name
               }}</label>
               <label
                  style="font-size: 11px; color: #666666; margin-bottom: 0; font-weight: 400"
                  v-if="hasManyLocations"
                  >{{ location || '' }}</label
               >
            </div>
            <button
               v-if="!isSME"
               :disabled="isCompleted"
               :id="`btn-delete-vendor-${id}`"
               class="icon-btn delete-button"
               type="button"
               @click="deleteManualVendor(id)"
            >
               <img src="@/assets/img/delete.png" style="width: 22px; height: 28px" alt="delete" />
            </button>
         </div>
      </div>
      <SupportingDocumnets :expense-type="expenseType" :disabled="isCompleted" />
   </div>
   <div style="margin-bottom: 25px" v-else>
      <ContractorsManualFlow />
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import SupportingDocumnets from './SupportingDocumnets.vue';
import ContractorsManualFlow from './contractors/ContractorsManualFlow.vue';
import EmployeesManualFlow from './employees/EmployeesManualFlow.vue';

export default {
   components: {
      ContractorsManualFlow,
      EmployeesManualFlow,
      SupportingDocumnets,
   },

   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         addManually: false,
         manuallyAddedName: '',
         location: '',
         hasBlurred: false,
         waiting: false,
      };
   },

   computed: {
      ...mapGetters({
         isSME: 'isSME',
         currentCompany: 'companies/currentCompany',
         usStates: 'usStates',
      }),

      manualVendors() {
         return this.$store.getters[`${this.expenseType}/manualVendors`];
      },

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },

      companyId() {
         return this.currentCompany.id;
      },

      hasManyLocations() {
         return this.declaration === 'MANY';
      },

      stateOptions() {
         return Object.entries(this.usStates).map(([key, value]) => ({text: value, value: key}));
      },

      nameExists() {
         const normalizedName = this.manuallyAddedName.trim();
         const normalizedLocation = (this.location || '').trim();

         return this.manualVendors.some(
            ({name, location}) =>
               name.trim() === normalizedName &&
               (!this.hasManyLocations || (location || '').trim() === normalizedLocation)
         );
      },

      inputState() {
         if (!this.hasBlurred || this.waiting) return null;
         return !this.nameExists;
      },

      errorMessage() {
         if (!this.hasBlurred || this.waiting) return '';
         if (this.manuallyAddedName === '') return 'Name is required.';
         return this.nameExists ? 'A vendor with this name and location already exists' : '';
      },

      addVendorBtnDisabled() {
         return (
            this.nameExists ||
            this.manuallyAddedName === '' ||
            (this.hasManyLocations && this.location === '')
         );
      },
   },

   methods: {
      resetState() {
         this.addManually = false;
         this.manuallyAddedName = '';
         this.location = '';
         this.hasBlurred = false;
         this.waiting = false;
      },

      addManualVendor() {
         this.addManually = true;
      },

      async deleteManualVendor(id) {
         const proceed = await this.$bvModal.msgBoxConfirm(
            'Are you sure you want to delete this vendor?',
            {
               id: 'modal-confirm-delete',
               title: 'Delete this vendor?',
               centered: true,
               okVariant: 'danger',
            }
         );
         if (proceed) {
            await this.$store.dispatch(`${this.expenseType}/deleteManualVendor`, {
               vendorId: id,
               companyId: this.companyId,
               force: true,
            });
         }
      },

      async saveVendor() {
         this.waiting = true;
         try {
            await this.$store.dispatch(`${this.expenseType}/createManualVendor`, {
               companyId: this.companyId,
               name: this.manuallyAddedName,
               location: this.location,
            });
            this.addManually = false;
            this.manuallyAddedName = '';
            this.location = '';
            this.hasBlurred = false;
         } finally {
            this.waiting = false;
         }
      },

      handleBlur() {
         this.hasBlurred = true;
      },
   },
};
</script>

<style lang="scss" scoped>
.modal-subtitle {
   padding-top: 10px;
   font-size: 14px;
   font-weight: 400;
   color: black;
   margin-right: 20px;
}

.project-section {
   border-radius: 5px;
   background-color: #f9f9f9;
   padding: 10px 20px;
   margin-bottom: 10px;
   display: flex;
   flex-direction: row;
   min-width: 100%;
   align-items: center;
   height: 60px;
}

.my-next-ok-btn {
   padding: 5px 30px;
   font-size: 13px !important;
   background-color: #0072cc;
   border: 1px solid #1660a9;
   &:hover {
      background-color: #0072cc;
      border: 1px solid #1660a9;
   }
}

.btn-disabled {
   background-color: #999 !important;
   border-color: #999 !important;
   &:hover {
      background-color: #999 !important;
      border-color: #999 !important;
   }
}

.delete-button {
   color: grey !important;
}
</style>
